import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';

export interface ContractIntelData {
  isVerified: boolean | null;
  isProxy: boolean | null;
  buyTaxPercent: number | null;
  sellTaxPercent: number | null;
  isOpenSource: boolean | null;
  contractRiskLevel: string | null;
  contractAgeDays?: number | null;
  dataUnavailableReason?: string;
}

const NO_DATA = 'No data available yet';

export function ContractIntelPanel({ data }: { data: ContractIntelData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  if (d.dataUnavailableReason) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Contract Intelligence
          <InfoTooltip explain={METRIC_EXPLANATIONS.sellabilityCheck} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Contract Analysis</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Contract Intelligence
        <InfoTooltip explain={METRIC_EXPLANATIONS.sellabilityCheck} compact />
      </h3>
      <div className="intel-grid intel-grid-3">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Risk Assessment</span>
            {d.contractRiskLevel ? (
              <span className={`intel-bool-badge ${d.contractRiskLevel === 'LOW' ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.contractRiskLevel === 'LOW' ? 'Low Risk Indicators' : d.contractRiskLevel === 'HIGH' ? 'Severe Risk Indicators' : 'Moderate Risk Indicators'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Contract Age
              <InfoTooltip explain={METRIC_EXPLANATIONS.contractAge} compact />
            </span>
            <span className="intel-metric-value">
              {d.contractAgeDays != null ? `${Math.round(d.contractAgeDays)} days` : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Code Verified</span>
            {d.isVerified != null ? (
              <span className={`intel-bool-badge ${d.isVerified ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.isVerified ? 'Yes' : 'No'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Open Source Code</span>
            {d.isOpenSource != null ? (
              <span className={`intel-bool-badge ${d.isOpenSource ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.isOpenSource ? 'Yes' : 'No'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Upgradeable Contract</span>
            {d.isProxy != null ? (
              <span className={`intel-bool-badge ${!d.isProxy ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.isProxy ? 'Yes' : 'No'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Buy Tax
              <InfoTooltip explain={METRIC_EXPLANATIONS.buyTax} compact />
            </span>
            <span className="intel-metric-value" style={{ color: d.buyTaxPercent != null ? (d.buyTaxPercent > 10 ? '#e74c3c' : d.buyTaxPercent > 5 ? '#f39c12' : '#27ae60') : undefined }}>
              {d.buyTaxPercent != null ? `${d.buyTaxPercent}%` : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Sell Tax
              <InfoTooltip explain={METRIC_EXPLANATIONS.sellTax} compact />
            </span>
            <span className="intel-metric-value" style={{ color: d.sellTaxPercent != null ? (d.sellTaxPercent > 10 ? '#e74c3c' : d.sellTaxPercent > 5 ? '#f39c12' : '#27ae60') : undefined }}>
              {d.sellTaxPercent != null ? `${d.sellTaxPercent}%` : NO_DATA}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
